import axios from "axios";
import {
  refreshAccessTokenInterceptor,
  refreshAccessToken,
} from "../utils/refreshTokens";
import { fullAPIURL } from "../utils/requests";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";
import { CelebrationType } from "./metricsApi";
import { PlanForm } from "src/pages/Plans/PlanDrawer/PlanDrawer.types";
import { getAccessToken } from "../utils/tokens";
import { z } from "zod";
import { AIMessage } from "src/pages/Dashboard/types";

const aiAxios = axios.create({
  baseURL: `${fullAPIURL}/v2/ai`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

aiAxios.interceptors.request.use(requestAccessTokenInterceptor);

aiAxios.interceptors.request.use(refreshAccessTokenInterceptor);

aiAxios.interceptors.response.use(responseStatusInterceptor);

export type CompletionResponse = {
  completion: string;
};

export type TextType =
  | "confirmationMessage"
  | "shortDescription"
  | "longDescription";

export async function postOutreachSms({
  memberName,
  gymName,
  numberOfYears,
  celebrationType,
}: {
  memberName: string;
  gymName: string;
  numberOfYears?: number;
  celebrationType: CelebrationType;
}): Promise<CompletionResponse> {
  if (celebrationType === "birthday") {
    const response = await aiAxios.post("/outreach/sms/birthday", {
      memberName,
      gymName,
    });
    return response.data;
  } else if (celebrationType === "anniversary") {
    const response = await aiAxios.post("/outreach/sms/anniversary", {
      memberName,
      gymName,
      numberOfYears,
    });
    return response.data;
  } else {
    throw new Error("Invalid celebration type");
  }
}

export async function generatePlanText({
  textType,
  plan,
}: {
  textType: TextType;
  plan: PlanForm;
}): Promise<CompletionResponse> {
  const response = await aiAxios.post("/generate-plan-text", {
    type: textType,
    plan,
  });
  return response.data;
}

export type ChatResponse = {
  title: string;
  chatUuid: string;
  response: any;
};

export type GetChatsItem = {
  chatUuid: string;
  title: string;
  createdOn: string;
  updatedOn: string;
  clientUuid: string;
  userUuid: string;
};

export type GetChatsResult = {
  chats: GetChatsItem[];
};

export type GetHistoricalChatResult = {
  chatUuid: string;
  title: string;
  createdOn: string;
  updatedOn: string;
  clientUuid: string;
  userUuid: string;
  chatHistory: AIMessage[];
};

export async function getChats(): Promise<GetChatsResult> {
  const response = await aiAxios.get("/chats");
  return response.data;
}

export async function getHistoricalChat(
  chatUuid: string,
): Promise<GetHistoricalChatResult> {
  const response = await aiAxios.get(`/chats/${chatUuid}`);
  return response.data;
}

export async function deleteChat(chatUuid: string): Promise<void> {
  await aiAxios.delete(`/chats/${chatUuid}`);
}

export async function renameChat(
  chatUuid: string,
  newTitle: string,
): Promise<any> {
  const response = await aiAxios.put(`/chats/${chatUuid}`, {
    title: newTitle,
  });
  return response.data;
}

export async function postChat({
  message,
  chatUuid,
  interruptionStatus,
}: {
  message: string;
  chatUuid?: string;
  interruptionStatus: "approved" | "rejected";
}): Promise<Response> {
  const accessToken = getAccessToken();
  if (accessToken) {
    try {
      await refreshAccessToken(accessToken);
    } catch (error) {
      throw new Error("Failed to refresh token");
    }
  }

  // Get the potentially refreshed token
  const currentToken = getAccessToken();

  const response = await fetch(
    `${fullAPIURL}/v2/ai/chat/v2${chatUuid ? "/" + chatUuid : ""}`,
    {
      method: "POST",
      mode: "cors", // Explicitly set CORS mode
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ message, interruptionStatus }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response;
}

export const ClassScheduleExtractorResponse = z.object({
  classes: z.array(
    z.object({
      title: z.string().optional(),
      startTime: z.string().optional(),
      endTime: z.string().optional(),
      dayOfWeek: z.string().optional(),
    }),
  ),
});

export type ClassScheduleExtractorResponse = z.infer<
  typeof ClassScheduleExtractorResponse
>;

export async function extractClassSchedule({
  type,
  content,
}: {
  type: "text" | "image";
  content: string;
}): Promise<ClassScheduleExtractorResponse> {
  const response = await aiAxios.post("/schedule-extractor", { type, content });
  return ClassScheduleExtractorResponse.parse(response.data);
}
